import { Box, Checkbox, TableRow, Typography } from "@mui/material";
import { format, getWeek, isValid } from "date-fns";
import { useTranslation } from "react-i18next";
import { epochToDate, getWorkTimeDuration } from "../../../app/common";
import { useAppSelector } from "../../../app/hooks";
import InfoTooltip from "../../../common/components/functional/InfoTooltip/InfoTooltip";
import { FlexVerticalAligned } from "../../../common/components/styled/styled";
import { formatLeadingZerosDateWithDayOfWeek } from "../../../common/helpers/formatDateTime";
import { Entry, Expense } from "../entriesAPI";
import { isLeaveAttachmentRequiredButMissing } from "../Entry/Shared/functions";
import {
  EntriesTableCell,
  PrimaryLightTextBox,
  RoundTextContainer,
} from "../styles";
import { CollabsibleStates, Refs } from "../types";
import EntriesCollapsibleTable from "./EntriesCollapsibleTable";
import EntriesWorkDetailsBreadcrumbs from "./EntriesWorkDetailsBreadcrumbs";
import { MONTHLY_SALARY_EXPENSE_CODE } from "../constants";

type Props = {
  item: Entry;
  index: number;
  handleSelectSingleEntriesCheckbox: (
    event: React.MouseEvent<HTMLTableCellElement, MouseEvent>,
    selectedEntry: Entry,
  ) => void;
  handleSingleEntryRowClick: (singleEntry: Entry) => void;
  selectedEntries: Entry[];
  collabsibleStates: CollabsibleStates;
  headers: {
    supplementHeaders: string[];
    supplementsHeaderRefs: Refs<any[]>;
    expensesHeaders: string[];
    expensesHeaderRefs: Refs<any[]>;
  };
  filterTab: number;
};

const getUniqueExpenseTypesLength = (obj: { [key: string]: Expense }) => {
  if (obj) {
    // Don't show monthly salary expenses (YLPE-7610)
    const expenseTypes = Object.values(obj)
      .map((e) => e.expenseTypeCode)
      .filter((code) => code !== MONTHLY_SALARY_EXPENSE_CODE);
    return [...new Set(expenseTypes)].length;
  }
};

const getWorkTimeRange = (start: Date, end: Date) => {
  if (!(isValid(start) || isValid(end))) return "";

  const startTimeHour = format(start, "HH:mm");
  const endTimeHour = format(end, "HH:mm");
  return startTimeHour && endTimeHour
    ? `${startTimeHour} - ${endTimeHour}`
    : "";
};

const GroupEntryRow = ({
  item,
  index,
  handleSelectSingleEntriesCheckbox,
  handleSingleEntryRowClick,
  selectedEntries,
  collabsibleStates,
  headers,
  filterTab,
}: Props) => {
  const { t } = useTranslation("hourEntries");
  const { workTitle: workTitleTranslations } = useAppSelector(
    (state: any) => state.localized,
  );

  const translatedEntryTypes = useAppSelector(
    (state: any) => state.localized.entryType,
  );

  const even = index % 2 === 0;

  const startDate = epochToDate(item.start);
  const endDate = epochToDate(item.end);

  const formattedDate = formatLeadingZerosDateWithDayOfWeek(
    new Date(startDate),
  );

  const week = getWeek(startDate, { weekStartsOn: 1 });

  const timeRange = getWorkTimeRange(startDate, endDate);

  const duration = getWorkTimeDuration(
    endDate,
    startDate,
    item.lunchBreak?.duration,
  );

  const breadcrumbs = [
    item.workLocationName,
    workTitleTranslations[item.workTitleId],
    item.customerCostCenterName,
  ];

  const checked = selectedEntries.some(
    (selectedEntry) => selectedEntry.id === item.id,
  );

  // Go through approvalStateChanges and show the lastest change that has comment (customer's feedback)
  const latestCustomerComment =
    item?.approvalStateChanges
      ?.filter((change) => change?.comment) // Filter out changes without a comment or with an empty string as the comment
      .sort((a, b) => b.timestamp - a.timestamp)[0]?.comment || ""; // Sort by timestamp in descending order // Take the first item's comment, which is the latest one

  const renderEntryType = () => {
    if (item.type === "WORK_ENTRY") return null;
    const translatedEntryType = translatedEntryTypes[item.type];
    const isLeaveAttachmentMissing = isLeaveAttachmentRequiredButMissing(item);

    return (
      <FlexVerticalAligned gap="4px" justifyContent="center" mb="5px">
        <Typography fontSize="14px" color="#C66700">
          {translatedEntryType}
        </Typography>
        {isLeaveAttachmentMissing && (
          <InfoTooltip
            variant="error"
            style={{ width: "18px", height: "18px" }}
            title={t("hourEntries:leaveAttachmentRequiredAndMissing")}
          />
        )}
      </FlexVerticalAligned>
    );
  };

  return (
    <TableRow
      data-id={item.id}
      className={`row_${even ? "even" : "odd"}`}
      key={item.id}
      onClick={() => handleSingleEntryRowClick(item)}
      sx={{ cursor: "pointer" }}
    >
      <EntriesTableCell
        padding="checkbox"
        noBorder
        onClick={(event) => handleSelectSingleEntriesCheckbox(event, item)}
      >
        <Checkbox checked={checked} sx={{ padding: 0 }} />
      </EntriesTableCell>
      <EntriesTableCell align="center">{week}</EntriesTableCell>
      <EntriesTableCell
        sx={{
          overflow: "visible",
          maxWidth: "100px",
          position: "relative",
        }}
      >
        <Box
          sx={{
            height: "53px",
            minWidth: "90px",
          }}
        >
          {formattedDate}
        </Box>
        <EntriesWorkDetailsBreadcrumbs
          sx={{
            backgroundColor:
              filterTab === 1
                ? even
                  ? "#FFF3E0"
                  : "rgba(255, 250, 242, 1)"
                : even
                  ? "#FAFAFB"
                  : "#FFF",
          }}
          breadcrumbs={breadcrumbs}
        />
      </EntriesTableCell>
      <EntriesTableCell>
        <PrimaryLightTextBox>
          <Typography sx={{ whiteSpace: "nowrap", lineHeight: 1 }}>
            {timeRange}
          </Typography>
          <Typography
            variant="header"
            sx={{ whiteSpace: "nowrap", fontWeight: 600 }}
          >
            {`(${duration})`}
          </Typography>
        </PrimaryLightTextBox>
      </EntriesTableCell>
      {/*
      <EntriesTableCell align="center">
        <RoundTextContainer variant="header">
          {getObjectKeysLength(item.supplements)}
        </RoundTextContainer>
      </EntriesTableCell>
   
  <EntriesCollapsibleTable
        open={collabsibleStates.supplementsOpen}
        items={item.supplements}
        headers={headers.supplementHeaders}
        refs={headers.supplementsHeaderRefs}
      />
        */}

      <EntriesTableCell align="center" width="10px">
        <RoundTextContainer variant="header">
          {getUniqueExpenseTypesLength(item.expenses)}
        </RoundTextContainer>
      </EntriesTableCell>
      <EntriesCollapsibleTable
        open={collabsibleStates.expensesOpen}
        items={item.expenses}
        headers={headers.expensesHeaders}
        refs={headers.expensesHeaderRefs}
      />
      <EntriesTableCell>
        {renderEntryType()}
        {
          // If entry has feedback from talent, show it
          item?.feedback?.comment && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
              }}
            >
              <Box
                sx={{
                  marginRight: "5px",
                  color: "rgba(0, 0, 0, 0.38)",
                }}
              >
                {`${t("hourEntries:TALENT")}: `}
              </Box>
              <Box color={"black"}>{item.feedback.comment}</Box>
            </Box>
          )
        }
        {latestCustomerComment !== "" && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
            }}
          >
            <Box
              sx={{
                marginRight: "5px",
                color: "rgba(0, 0, 0, 0.38)",
              }}
            >
              {`${t("hourEntries:CUSTOMER_ORG")}: `}
            </Box>
            <Box color={"black"}>{latestCustomerComment}</Box>
          </Box>
        )}
      </EntriesTableCell>
      <EntriesTableCell
        sx={{
          color: item.approvalState === "Approved" ? "#00A600" : "error.main",
        }}
      >
        {`${item.approvalState === "New" ? "" : t(item.approvalState)}`}
      </EntriesTableCell>
    </TableRow>
  );
};

export default GroupEntryRow;
